import { useCallback, useMemo } from 'react';
import { useApiClient } from 'hooks/useApiClient';
import { API_ROUTES } from 'utils/constants';
import { clientAuthorizedGet, clientAuthorizedPost } from 'utils/fetchUtils';
import {
  Membership,
  MembershipStatistics,
  CreateMembershipRequest,
  UpdateMembershipRequest,
} from 'types/membership';
import { sortMembershipHistoryByUpdatedAtDesc } from 'utils/membershipHistoryUtils';
import { sortMembershipsByLastThenFirstName } from 'utils/membershipListUtils';

export function useMembershipService() {
  const apiClient = useApiClient();

  const createMembership = useCallback(
    async (membership: CreateMembershipRequest): Promise<Membership> => {
      return apiClient.post<Membership>(
        API_ROUTES.MEMBERSHIPS,
        membership as unknown as Record<string, unknown>,
      );
    },
    [apiClient],
  );

  /**
   * GET `/memberships?companyId=…&includeVoided=…` — all membership records for the company.
   * Voided rows are omitted unless `includeVoided` is true (API default: false).
   * Response order: last name, then first name.
   */
  const listMemberships = useCallback(
    async (includeVoided = false): Promise<Membership[]> => {
      const queryParams = includeVoided ? { includeVoided: 'true' } : undefined;
      const rows = await apiClient.get<Membership[]>(
        API_ROUTES.MEMBERSHIPS,
        queryParams,
      );
      return sortMembershipsByLastThenFirstName(rows);
    },
    [apiClient],
  );

  const getMembership = useCallback(
    async (id: string): Promise<Membership> => {
      return apiClient.get<Membership>(`${API_ROUTES.MEMBERSHIPS}/${id}`);
    },
    [apiClient],
  );

  /**
   * Return aggregated statistics about all active (non-voided) membership records.
   */
  const getMembershipStatistics =
    useCallback(async (): Promise<MembershipStatistics> => {
      return apiClient.get<MembershipStatistics>(
        `${API_ROUTES.MEMBERSHIPS}/statistics`,
      );
    }, [apiClient]);

  /**
   * Restore a voided membership.
   * Backend creates a snapshot of the voided state and marks the membership active again.
   */
  const restoreMembership = useCallback(
    async (id: string): Promise<Membership> => {
      return apiClient.patch<Membership>(
        `${API_ROUTES.MEMBERSHIPS}/${id}/restore`,
        {},
      );
    },
    [apiClient],
  );

  /**
   * GET /memberships/pending-invitations (Bearer only; no companyId).
   * Returns membership invitations created by email where the invited person has not yet
   * been linked to a user account. Use this so an authenticated user can see companies
   * they have been invited to join (by the email they signed up with). Response rows
   * have `userId` unset and `email` matching the authenticated user; `company` is populated
   * for display.
   */
  const getPendingInvitations = useCallback(async (): Promise<Membership[]> => {
    return clientAuthorizedGet<Membership[]>(
      `${API_ROUTES.MEMBERSHIPS}/pending-invitations`,
      undefined,
      null,
    );
  }, []);

  const acceptPendingInvitation = useCallback(
    async (membershipId: string): Promise<Membership> => {
      return clientAuthorizedPost<Membership>(
        `${API_ROUTES.MEMBERSHIPS}/pending-invitations/${membershipId}/accept`,
        {},
        null,
      );
    },
    [],
  );

  const declinePendingInvitation = useCallback(
    async (membershipId: string): Promise<{ message: string }> => {
      return clientAuthorizedPost<{ message: string }>(
        `${API_ROUTES.MEMBERSHIPS}/pending-invitations/${membershipId}/decline`,
        {},
        null,
      );
    },
    [],
  );

  /**
   * Revert the membership to a previous snapshot.
   * Current state is saved as a new snapshot before rollback.
   */
  const rollbackMembership = useCallback(
    async (id: string, snapshotId: string): Promise<Membership> => {
      return apiClient.patch<Membership>(
        `${API_ROUTES.MEMBERSHIPS}/${id}/rollback/${snapshotId}`,
        {},
      );
    },
    [apiClient],
  );

  /**
   * Return the membership and all historical snapshots (versions),
   * sorted by updatedAt descending (current entity first, then snapshots).
   */
  const getMembershipHistory = useCallback(
    async (id: string): Promise<Membership[]> => {
      const rows = await apiClient.get<Membership[]>(
        `${API_ROUTES.MEMBERSHIPS}/${id}/history`,
      );
      return sortMembershipHistoryByUpdatedAtDesc(rows);
    },
    [apiClient],
  );

  /**
   * PATCH `/memberships/:id?companyId=…` — updates the membership; a snapshot of the
   * previous state is created before applying changes. Response is the updated membership
   * (with `company` / `user` when present); the `versions` history collection is not included.
   */
  const updateMembership = useCallback(
    async (id: string, body: UpdateMembershipRequest): Promise<Membership> => {
      if (!apiClient.companyId) {
        throw new Error('No company selected.');
      }
      const endpoint = `${API_ROUTES.MEMBERSHIPS}/${id}`;
      return apiClient.patch<Membership>(
        endpoint,
        body as unknown as Record<string, unknown>,
      );
    },
    [apiClient],
  );

  const voidMembership = useCallback(
    async (id: string, reason?: string): Promise<Membership> => {
      const queryParams: Record<string, string> = {};
      if (reason) {
        queryParams.reason = reason;
      }
      const result = await apiClient.delete<Membership>(
        `${API_ROUTES.MEMBERSHIPS}/${id}`,
        undefined,
        queryParams,
      );
      if (!result) {
        throw new Error('Failed to void membership: No response from server');
      }
      return result;
    },
    [apiClient],
  );

  return useMemo(
    () => ({
      createMembership,
      listMemberships,
      getMembership,
      getMembershipStatistics,
      getPendingInvitations,
      acceptPendingInvitation,
      declinePendingInvitation,
      restoreMembership,
      getMembershipHistory,
      rollbackMembership,
      updateMembership,
      voidMembership,
    }),
    [
      createMembership,
      listMemberships,
      getMembership,
      getMembershipStatistics,
      getPendingInvitations,
      acceptPendingInvitation,
      declinePendingInvitation,
      restoreMembership,
      getMembershipHistory,
      rollbackMembership,
      updateMembership,
      voidMembership,
    ],
  );
}
